import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { isLoggedInAtom, userAtom } from '@/state/loginState';
import { api } from '@/api/repository/axios';
import { HomeSearchResponse } from '@/api/repository/types/types';
import { CONTENT_MAX_WIDTH } from '@/styles/layout';
import { extractCityId } from '@/utils/extractCityId';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// 아이콘 추가
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import NavRegionIcon from '@/assets/icons/NavRegionIcon';
import NavSubwayIcon from '@/assets/icons/NavSubwayIcon';
import NavJobIcon from '@/assets/icons/NavJobIcon';
import NavTopicIcon from '@/assets/icons/NavTopicIcon';

// 기존 레이아웃 시스템에 맞춰 수정
const MainContainer = styled.main`
  max-width: ${CONTENT_MAX_WIDTH};
  margin: 0 auto;
  padding: 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem 0;
    gap: 0.75rem;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  align-items: stretch; /* 배너와 로그인 박스 높이 맞추기 */
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const GridItem = styled.div<{ span?: number }>`
  grid-column: span ${props => props.span || 1} / span ${props => props.span || 1};
  min-width: 0;
  height: 100%;

  @media (max-width: 768px) {
    grid-column: span 1;
    height: auto;

    &.desktop-only {
      display: none;
    }
  }
`;

const LigBanner = styled.div`
  height: 100%;
  min-height: 80px;
  background-color: #1e3a8a;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-height: 80px;
    padding: 1rem;
  }
`;

const BannerImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 80px;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: auto;
  }
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 0.5rem;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  transform: translateZ(0);

  @media (max-width: 768px) {
    height: auto;
    object-fit: contain;
  }
`;

const BannerLabel = styled.span`
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  font-weight: 700;
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    top: 0.75rem;
    right: 1rem;
  }
`;

const BannerTitle = styled.h2`
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 800;
  margin-bottom: 0.25rem;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    line-height: 1.5rem;
    margin-bottom: 0.5rem;
  }
`;

const BannerSubtitle = styled.p`
  color: #d1d5db;
  font-size: 0.875rem;
  line-height: 1.25rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    line-height: 1.125rem;
  }
`;

const LoginBanner = styled.div`
  height: 100%;
  background-color: #eef2ff;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: auto;
    min-height: 80px;
    padding: 1rem;
  }
`;

const LoginTextContainer = styled.div``;

const LoginText = styled.p`
  font-weight: 500;
  color: #374151;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const LoginHighlight = styled.p`
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.75rem;
  color: #1f2937;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;

const LoginLink = styled.span`
  color: #2563eb;
`;

const CharacterIcon = styled.div`
  width: 4rem;
  height: 4rem;
  background-color: #c7d2fe;
  border-radius: 50%;

  @media (max-width: 768px) {
    width: 3rem;
    height: 3rem;
  }
`;

const UserTypeTag = styled.span`
  background-color: #dbeafe;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 1rem;
  white-space: nowrap;
`;

const Card = styled.div<{ scrollable?: boolean }>`
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  height: 255px; /* Fixed height based on current rendered size */
  overflow-y: ${props => props.scrollable !== false ? 'auto' : 'hidden'}; /* Default true, control scrolling via props */
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: auto;
    min-height: auto;
    overflow-y: visible;
    overflow-x: hidden;
    padding: 0.5rem;

    .region-tabs-section {
      display: none;
    }
  }
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  @media (max-width: 768px) {
    gap: 0.15rem;
  }
`;

const PostListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  padding: 3px 0;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }

  & > span:first-of-type {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.35rem 0;
  }
`;

const TagChip = styled.span`
  display: inline-block;
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #eef2ff;
  color: #4b5563;
  border-radius: 9999px;
  margin-left: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 0.688rem;
    padding: 0.313rem 0.5rem;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  line-height: 1.5rem;
  font-weight: 700;
  margin-top: 0.5rem;
  margin-bottom: 0.625rem;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    line-height: 1.5rem;
    margin-top: 0.25rem;
    margin-bottom: 0.5rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.375rem;
`;

const SeeAllLink = styled.a`
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #6b7280;
  text-decoration: none;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    color: #374151;
  }
`;

const ChipContainer = styled.div<{ gridLayout?: boolean }>`
  display: ${props => props.gridLayout ? 'grid' : 'flex'};
  ${props => props.gridLayout && `
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 0.25rem;
  `}
  ${props => !props.gridLayout && `
    flex-wrap: wrap;
    gap: 0.5rem;
  `}
  margin-top: 0.6rem;
  margin-bottom: 0.875rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: 0.25rem;
    margin-bottom: 0.25rem;
  }
`;

const ChipButton = styled.button`
  font-size: 0.75rem;
  line-height: 0.7rem;
  font-weight: 500;
  padding: 0.32rem 0.6rem;
  background-color: #f3f4f6;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background-color: #e5e7eb;
  }

  @media (max-width: 768px) {
    font-size: 0.688rem;
    line-height: 0.938rem;
    padding: 0.4rem 0.6rem;
    flex: 0 0 auto;
  }
`;

const MenuGridWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    margin-top: 1rem;
    width: 100%;
    box-sizing: border-box;
  }
`;

const MenuGrid = styled.div`
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    padding: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const MenuItem = styled.div`
  @media (max-width: 768px) {
    background: #ffffff;
    border-radius: 0.375rem;
    padding: 6px 2px;
    text-align: center;
    border: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:active {
      background-color: #f3f4f6;
    }

    svg {
      font-size: 1rem;
      color: #6b7280;
      margin-bottom: 1px;
    }

    span {
      font-size: 9px;
      font-weight: 600;
      color: #6b7280;
    }
  }
`;


const SubCardTitle = styled.h4`
  font-weight: 700;
  font-size: 0.875rem;
  margin: 0 0 0.2rem 0;
  line-height: 1.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubCardDescription = styled.p`
  font-size: 0.75rem;
  line-height: 1rem;
  color: #6b7280;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RegionTabContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  line-height: 1.75rem;
  font-weight: 700;
  /* margin is no longer needed here, SectionHeader will handle it */

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;

const RegionTabButton = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  /* Inherit typography from container */
  font-size: inherit;
  line-height: inherit;
  font-family: inherit;

  /* Style based on active state */
  font-weight: ${props => props.active ? '700' : '400'};
  color: ${props => props.active ? '#1f2937' : '#6b7280'};
`;

const Separator = styled.span`
  margin: 0 0.5rem;
`;

const SeminarListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem; /* Further reduced gap */
  flex: 1; /* Take remaining space after header */
`;

const SeminarListItem = styled.div`
  display: flex;
  gap: 0;
  padding: 0;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  background-color: white;
  height: 85px;
  min-height: 85px;
  align-items: stretch;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
`;

const SeminarItemThumbnailImg = styled.img`
  /* 카드 높이(85px) 기준 16:9 비율 (85 * 16/9 ≈ 151px) */
  width: 151px;
  height: 100%;
  object-fit: cover;
  object-position: center;
  background: #f9fafb;
  flex-shrink: 0;
  border-radius: 0.75rem 0 0 0.75rem;
`;

const SeminarItemPlaceholder = styled.div`
  width: 151px;
  height: 100%;
  background: #f5f6f8;
  flex-shrink: 0;
  border-radius: 0.75rem 0 0 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #999;
  text-align: center;
  padding: 4px;
`;

const SeminarThumbnail = ({ url }: { url?: string }) => {
    const [error, setError] = useState(false);
    if (!url || error) {
        return <SeminarItemPlaceholder>이미지<br/>준비중</SeminarItemPlaceholder>;
    }
    return <SeminarItemThumbnailImg src={url} onError={() => setError(true)} />;
};

const SeminarItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
  padding: 0.625rem;
`;

const SeminarItemMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.15rem;
`;

const SeminarItemPrice = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #059669;
`;

const SeminarCarousel = styled(Slider)`
  .slick-dots {
    display: none !important;
  }
  
  .slick-prev, .slick-next {
    display: none !important;
  }
`;

const SeminarSlide = styled.div`
  display: flex !important;
  flex-direction: column;
  gap: 0.375rem;
`;

// 배너 타입 정의
interface HomeBanner {
    id: number;
    title?: string;
    imageUrl: string;
    mobileImageUrl?: string;
    linkUrl?: string;
    displayOrder: number;
}

import { HomeData } from '@/pages/Home/Home';

interface HomeContentsProps {
    homeData: HomeData | null;
}

function HomeContents({ homeData }: HomeContentsProps) {
    const navigate = useNavigate();
    const isLoggedIn = useRecoilValue(isLoggedInAtom);
    const user = useRecoilValue(userAtom);
    const [activeRegionTab, setActiveRegionTab] = useState('cities');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // 카테고리 클릭 핸들러
    const handleCategoryClick = (categoryId: number) => {
        switch (categoryId) {
            case 1: // 지역별 - PC의 '근처'와 동일하게 내 지역 채용 표시
                const nearbyCity = homeData?.cities?.find(city => city.name === '근처');
                if (nearbyCity) {
                    const cityId = extractCityId(nearbyCity.id);
                    navigate(`/recruitments/region?category=REGION&tab=region&districts=${cityId}_${nearbyCity.id}`);
                } else {
                    // 근처 정보가 없으면 기본 지역 목록으로 이동
                    navigate('/recruitments/region?category=REGION&tab=region');
                }
                break;
            case 2: // 호선별
                navigate('/recruitments/region?category=SUBWAY&tab=subway');
                break;
            case 3: // 직무별
                navigate('/recruitments/region?category=REGION&tab=job');
                break;
            case 4: // 오늘의 토픽
                navigate('/posts');
                break;
        }
    };

    // 카테고리 데이터
    const categories = [
        { id: 1, label: '지역별', icon: <NavRegionIcon width={22} height={22} color="#6b7280" /> },
        { id: 2, label: '호선별', icon: <NavSubwayIcon width={22} height={22} color="#6b7280" /> },
        { id: 3, label: '직무별', icon: <NavJobIcon width={22} height={22} color="#6b7280" /> },
        { id: 4, label: '오늘의 토픽', icon: <NavTopicIcon width={22} height={22} color="#6b7280" /> }
    ];

    // props로 받은 데이터 사용
    const topBanners = homeData?.topBanners || [];
    const welfareChips = homeData?.welfareChips || [];
    const hotPosts = homeData?.hotPosts || [];
    const todaySeminars = homeData?.todaySeminars || [];

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleRegionClick = (regionId: string, regionName: string) => {
        // 지역별 채용 페이지로 라우팅
        if (regionId === '0' || regionName === '전체') {
            // "전체" 선택시 파라미터를 0으로 설정
            navigate(`/recruitments/region?category=REGION&tab=region&districts=0`);
        } else if (regionName === '근처') {
            // "근처" 선택시 cityId_districtId 형태로 설정
            const cityId = extractCityId(regionId);
            navigate(`/recruitments/region?category=REGION&tab=region&districts=${cityId}_${regionId}`);
        } else {
            // 특정 지역 선택시 cityId_0 형태로 설정
            navigate(`/recruitments/region?category=REGION&tab=region&districts=${regionId}_0`);
        }
    };

    const handleJobTaskClick = (taskType: string) => {
        // 직무별 채용 페이지로 라우팅 (jobTasks 파라미터에 taskType 사용)
        navigate(`/recruitments/region?category=REGION&tab=job&jobTasks=${taskType}`);
    };

    const handleSubwayLineClick = (areaId: number) => {
        navigate(`/recruitments/region?category=SUBWAY&tab=subway&subwayArea=${areaId}`);
    };

    const handleWelfareChipClick = (value: string) => {
        // 복지 칩 클릭 시 근처 지역으로 설정하고 해당 복지 섹션으로 이동
        const nearbyCity = homeData?.cities?.find(city => city.name === '근처');

        if (nearbyCity) {
            // "근처"가 있으면 cityId_districtId 형태로 설정
            const cityId = extractCityId(nearbyCity.id);
            navigate(`/recruitments/region?category=REGION&tab=region&districts=${cityId}_${nearbyCity.id}&welfare=${value}`);
        } else {
            // "근처"가 없으면 서울(1100000000_0)로 설정
            navigate(`/recruitments/region?category=REGION&tab=region&districts=1100000000_0&welfare=${value}`);
        }
    };

    // 현재 활성 탭에 따른 데이터 반환
    const getCurrentTabData = () => {
        if (!homeData) return [];
        
        switch (activeRegionTab) {
            case 'cities':
                return homeData.cities || [];
            case 'jobTasks':
                return homeData.jobTasks || [];
            case 'subwayLines':
                return homeData.subwayLines || [];
            default:
                return homeData.cities || [];
        }
    };

    // 현재 활성 탭에 따른 클릭 핸들러 반환
    const getCurrentTabClickHandler = () => {
        switch (activeRegionTab) {
            case 'cities':
                return (item: any) => handleRegionClick(item.id, item.name);
            case 'jobTasks':
                return (item: any) => handleJobTaskClick(item.type);
            case 'subwayLines':
                return (item: any) => handleSubwayLineClick(item.id);
            default:
                return (item: any) => handleRegionClick(item.id, item.name);
        }
    };

    const currentTabData = getCurrentTabData();
    const currentClickHandler = getCurrentTabClickHandler();

    // 배너 클릭 핸들러
    const handleBannerClick = (banner: HomeBanner) => {
        if (banner.linkUrl) {
            window.open(banner.linkUrl, '_blank');
        }
    };

    return (
        <MainContainer>
            <GridContainer>
                <GridItem span={2}>
                    {topBanners.length > 0 ? (
                        <BannerImageWrapper onClick={() => handleBannerClick(topBanners[0])}>
                            <BannerImg
                                src={isMobile && topBanners[0].mobileImageUrl ? topBanners[0].mobileImageUrl : topBanners[0].imageUrl}
                                alt={topBanners[0].title || '배너'}
                            />
                        </BannerImageWrapper>
                    ) : (
                        <LigBanner style={{ backgroundColor: '#1e3a8a' }}>
                            <BannerLabel>국내 1위 채용서비스</BannerLabel>
                            <BannerTitle>치과 위생사들이 가장 많이 찾는 플랫폼</BannerTitle>
                            <BannerSubtitle>채용과 성장을 한번에 지원합니다</BannerSubtitle>
                        </LigBanner>
                    )}
                </GridItem>
                <GridItem className="desktop-only">
                    {isLoggedIn && user ? (
                        <LoginBanner style={{ cursor: 'pointer' }} onClick={() => navigate('/profile')}>
                            <LoginTextContainer>
                                <LoginText>안녕하세요,</LoginText>
                                <LoginHighlight><LoginLink>{user.name}</LoginLink>님</LoginHighlight>
                            </LoginTextContainer>
                            <UserTypeTag>
                                {user.userType === 'DENTAL_HYGIENIST' ? '치과위생사' :
                                 user.userType === 'DENTAL_TECHNICIAN' ? '치과기공사' :
                                 user.userType === 'DENTIST' ? '치과의사' :
                                 user.userType === 'CLINIC' ? '치과 관계자' :
                                 user.userType === 'STUDENT' ? '학생' : user.userType}
                            </UserTypeTag>
                        </LoginBanner>
                    ) : (
                        <LoginBanner style={{ cursor: 'pointer' }} onClick={() => navigate('/login')}>
                            <LoginTextContainer>
                                <LoginText>명치사의 모든 서비스를 이용하려면?</LoginText>
                                <LoginHighlight>지금 바로 <LoginLink>로그인</LoginLink> 해보세요</LoginHighlight>
                            </LoginTextContainer>
                            <CharacterIcon/>
                        </LoginBanner>
                    )}
                </GridItem>
            </GridContainer>

            <GridContainer>
            <GridItem>
                    <Card style={{ height: 'auto', minHeight: 'auto' }}>
                        <div>
                            <CardTitle>명치사 추천</CardTitle>
                            <ChipContainer>
                                {welfareChips.slice(0, 6).map(chip => (
                                    <ChipButton
                                        key={chip.value}
                                        onClick={() => handleWelfareChipClick(chip.value)}
                                    >
                                        {chip.label}
                                    </ChipButton>
                                ))}
                            </ChipContainer>
                        </div>

                        <div className="region-tabs-section">
                            <SectionHeader>
                                <RegionTabContainer>
                                    <RegionTabButton
                                        active={activeRegionTab === 'cities'}
                                        onClick={() => setActiveRegionTab('cities')}
                                    >
                                        지역별
                                    </RegionTabButton>
                                    <Separator>|</Separator>
                                    <RegionTabButton
                                        active={activeRegionTab === 'subwayLines'}
                                        onClick={() => setActiveRegionTab('subwayLines')}
                                    >
                                        호선별
                                    </RegionTabButton>
                                    <Separator>|</Separator>
                                    <RegionTabButton
                                        active={activeRegionTab === 'jobTasks'}
                                        onClick={() => setActiveRegionTab('jobTasks')}
                                    >
                                        직무별
                                    </RegionTabButton>
                                </RegionTabContainer>
                            </SectionHeader>
                            <ChipContainer gridLayout={activeRegionTab === 'cities'}>
                                {currentTabData.map((item: any) => (
                                    <ChipButton
                                        key={item.id || item.type}
                                        onClick={() => currentClickHandler(item)}
                                    >
                                        {item.name || item.description}
                                    </ChipButton>
                                ))}
                            </ChipContainer>
                        </div>
                    </Card>
                    <MenuGridWrapper>
                        <MenuGrid>
                            {categories.map((category) => (
                                <MenuItem key={category.id} onClick={() => handleCategoryClick(category.id)}>
                                    <div>{category.icon}</div>
                                    <span>{category.label}</span>
                                </MenuItem>
                            ))}
                        </MenuGrid>
                    </MenuGridWrapper>
                </GridItem>
                <GridItem className="desktop-only">
                    <Card scrollable={false}>
                        <SectionHeader>
                            <CardTitle>지금 HOT🔥 글</CardTitle>
                            <SeeAllLink onClick={() => navigate('/posts')}><ChevronRightIcon /></SeeAllLink>
                        </SectionHeader>

                        <PostList>
                            {hotPosts.slice(0, 5).map((post, index) => (
                                <PostListItem key={post.id || index} onClick={() => navigate(`/posts/${post.id}`)}>
                                    <span>{post.title}</span>
                                    <TagChip>조회 {post.viewCount}</TagChip>
                                </PostListItem>
                            ))}
                            {hotPosts.length === 0 && (
                                <PostListItem style={{ cursor: 'default' }}>
                                    <span>등록된 게시글이 없습니다.</span>
                                </PostListItem>
                            )}
                        </PostList>
                    </Card>
                </GridItem>

                <GridItem className="desktop-only">
                    <Card scrollable={false}>
                        <SectionHeader>
                            <CardTitle>오늘의 세미나!</CardTitle>
                            <SeeAllLink onClick={() => navigate('/seminars')}><ChevronRightIcon /></SeeAllLink>
                        </SectionHeader>

                        <SeminarListContainer>
                            {todaySeminars.length > 0 ? (
                                <SeminarCarousel
                                    dots={false}
                                    infinite={todaySeminars.length > 2}
                                    speed={500}
                                    slidesToShow={1}
                                    slidesToScroll={1}
                                    autoplay={todaySeminars.length > 2}
                                    autoplaySpeed={6000}
                                    arrows={false}
                                >
                                    {Array.from({ length: Math.ceil(todaySeminars.length / 2) }).map((_, slideIndex) => (
                                        <SeminarSlide key={slideIndex}>
                                            {todaySeminars.slice(slideIndex * 2, slideIndex * 2 + 2).map((seminar) => (
                                                <SeminarListItem
                                                    key={seminar.id}
                                                    onClick={() => navigate(`/seminars/${seminar.id}`)}
                                                >
                                                    <SeminarThumbnail url={seminar.thumbnailUrl} />
                                                    <SeminarItemContent>
                                                        <SubCardTitle>{seminar.title}</SubCardTitle>
                                                        <SubCardDescription>
                                                            {seminar.instructorNickname || seminar.instructor}
                                                        </SubCardDescription>
                                                        <SeminarItemMeta>
                                                            <SeminarItemPrice>
                                                                {(seminar.participationFee ?? 0) === 0
                                                                    ? '무료'
                                                                    : `₩${(seminar.participationFee ?? 0).toLocaleString()}`}
                                                            </SeminarItemPrice>
                                                        </SeminarItemMeta>
                                                    </SeminarItemContent>
                                                </SeminarListItem>
                                            ))}
                                        </SeminarSlide>
                                    ))}
                                </SeminarCarousel>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                                    등록된 세미나가 없습니다.
                                </div>
                            )}
                        </SeminarListContainer>
                    </Card>
                </GridItem>
            </GridContainer>
        </MainContainer>
    );
}

export default HomeContents;